import React, { useEffect, useState } from "react";
import { SkillArea } from "./Types";

interface ThoughtLeader {
  id: number;
  name: string;
  role_descriptor: string;
  image_url: string;
  image_alt: string;
  profile_path: string;
}

interface Props {
  thoughtLeaders: ThoughtLeader[];
  skillArea: SkillArea;
}

const PREVIEW_COUNT = 5;
const PAGE_SIZE = 8;

const pickRandom = <T,>(items: T[], count: number): T[] =>
  [...items].sort(() => Math.random() - 0.5).slice(0, count);

const replacementPositions = (length: number): number[] => {
  const count = Math.min(2, length);
  const positions: number[] = [];

  while (positions.length < count) {
    const position = Math.floor(Math.random() * length);

    if (!positions.includes(position)) {
      positions.push(position);
    }
  }

  return positions;
};

const replacePreviewLeaders = (
  previousLeaders: ThoughtLeader[],
  allLeaders: ThoughtLeader[],
  positions: number[],
) => {
  const nextLeaders = [...previousLeaders];
  const selectedIds = nextLeaders.map((leader) => leader.id);

  positions.forEach((position) => {
    const pool = allLeaders.filter(
      (leader) => !selectedIds.includes(leader.id),
    );
    const replacement = pool[Math.floor(Math.random() * pool.length)];

    if (replacement) {
      selectedIds[position] = replacement.id;
      nextLeaders[position] = replacement;
    }
  });

  return nextLeaders;
};

const Leaders = ({ thoughtLeaders, skillArea }: Props) => {
  const [previewLeaders, setPreviewLeaders] = useState<ThoughtLeader[]>([]);
  const [fadingPositions, setFadingPositions] = useState<number[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPreviewLeaders(pickRandom(thoughtLeaders, PREVIEW_COUNT));
    setFadingPositions([]);
    setShowAll(false);
    setPage(0);
  }, [thoughtLeaders]);

  useEffect(() => {
    if (showAll || thoughtLeaders.length <= PREVIEW_COUNT) {
      return undefined;
    }

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduced) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setPreviewLeaders((currentLeaders) => {
        const positions = replacementPositions(currentLeaders.length);
        setFadingPositions(positions);

        window.setTimeout(() => {
          setPreviewLeaders((latestLeaders) =>
            replacePreviewLeaders(latestLeaders, thoughtLeaders, positions),
          );

          window.setTimeout(() => {
            setFadingPositions([]);
          }, 400);
        }, 400);

        return currentLeaders;
      });
    }, 6000);

    return () => window.clearInterval(interval);
  }, [showAll, thoughtLeaders]);

  if (thoughtLeaders.length === 0) {
    return null;
  }

  const visibleThoughtLeaders = showAll
    ? thoughtLeaders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
    : previewLeaders;
  const pageCount = Math.ceil(thoughtLeaders.length / PAGE_SIZE);

  return (
    <section className="leaders" aria-labelledby="leaders-title">
      <div className="container">
        <div className="section-head">
          <h2 id="leaders-title">
            Learn from the people who <em>shaped the field</em>
          </h2>
        </div>

        <p
          className="leaders-sub"
          dangerouslySetInnerHTML={{
            __html: skillArea?.leaders_header_summary,
          }}
        ></p>

        <div className="instructor-row" id="leaderRow">
          {visibleThoughtLeaders.map((thoughtLeader, index) => (
            <a
              className={`instructor ${
                fadingPositions.includes(index) ? "fading" : ""
              }`}
              href={thoughtLeader.profile_path}
              key={thoughtLeader.id}
            >
              <div className="avatar">
                {thoughtLeader.image_url ? (
                  <img
                    src={thoughtLeader.image_url}
                    alt={thoughtLeader.image_alt || thoughtLeader.name}
                    loading="lazy"
                    width="96"
                    height="96"
                    decoding="async"
                  />
                ) : (
                  <span>[ Photo ]</span>
                )}
              </div>

              <div className="name">{thoughtLeader.name}</div>

              <div className="role">{thoughtLeader.role_descriptor}</div>
            </a>
          ))}
        </div>

        <p
          className="leaders-summary"
          dangerouslySetInnerHTML={{ __html: skillArea?.leaders_body_summary }}
        ></p>

        {!showAll && thoughtLeaders.length > PREVIEW_COUNT && (
          <button
            className="leaders-toggle"
            type="button"
            id="leadersToggle"
            aria-expanded={showAll}
            aria-controls="leaderRow"
            onClick={() => setShowAll(true)}
          >
            See all teachers
          </button>
        )}

        {showAll && pageCount > 1 && (
          <div className="leaders-pagination" aria-label="Teacher pages">
            <button
              className="leaders-page-button"
              type="button"
              disabled={page === 0}
              onClick={() => setPage((currentPage) => currentPage - 1)}
            >
              Previous
            </button>

            <span className="leaders-page-count">
              {page + 1} / {pageCount}
            </span>

            <button
              className="leaders-page-button"
              type="button"
              disabled={page === pageCount - 1}
              onClick={() => setPage((currentPage) => currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Leaders;
